import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent } from "../../lib/content";
import { Msg } from "../../components/admin/FormKit";
import { KeyRound, Save, Globe, Lock } from "lucide-react";

export default function AdminSettings() {
  const { r2, r2Creds, changePassword, updateR2Config } = useAuth();
  const { settings, saveSettings } = useContent();

  // --- site settings tab ---
  const [siteForm, setSiteForm] = useState(settings);
  const [marqueeInput, setMarqueeInput] = useState(
    (settings.marquee_words || []).join("\n")
  );
  const [siteMsg, setSiteMsg] = useState("");
  const [savingSite, setSavingSite] = useState(false);

  useEffect(() => {
    setSiteForm(settings);
    setMarqueeInput((settings.marquee_words || []).join("\n"));
  }, [settings]);

  const saveSite = async (e) => {
    e.preventDefault();
    setSavingSite(true);
    setSiteMsg("");
    try {
      await saveSettings(r2, {
        ...siteForm,
        marquee_words: marqueeInput
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setSiteMsg("تنظیمات سایت ذخیره شد ✓");
    } catch (e) {
      setSiteMsg(`خطا: ${e.message}`);
    } finally {
      setSavingSite(false);
    }
  };

  // --- password tab ---
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    setPwMsg("");
    if (pwNew.length < 6) {
      setPwMsg("رمز جدید حداقل ۶ کاراکتر باشد.");
      return;
    }
    if (pwNew !== pwNew2) {
      setPwMsg("دو رمز یکسان نیست.");
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(pwOld, pwNew);
      setPwMsg("رمز با موفقیت تغییر کرد ✓");
      setPwOld("");
      setPwNew("");
      setPwNew2("");
    } catch (e) {
      setPwMsg(e.message);
    } finally {
      setPwBusy(false);
    }
  };

  // --- r2 config tab ---
  const [r2Form, setR2Form] = useState({
    publicUrl: r2Creds?.publicUrl || "",
    endpoint: r2Creds?.endpoint || "",
  });
  const [r2Pw, setR2Pw] = useState("");
  const [r2Msg, setR2Msg] = useState("");
  const [r2Busy, setR2Busy] = useState(false);

  const submitR2 = async (e) => {
    e.preventDefault();
    setR2Msg("");
    setR2Busy(true);
    try {
      await updateR2Config(
        {
          publicUrl: r2Form.publicUrl.trim() || r2Creds?.publicUrl,
          endpoint: r2Form.endpoint.trim() || r2Creds?.endpoint,
        },
        r2Pw
      );
      setR2Msg("پیکربندی R2 به‌روزرسانی شد ✓");
      setR2Pw("");
    } catch (e) {
      setR2Msg(e.message);
    } finally {
      setR2Busy(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-settings-panel">
      {/* Site Settings */}
      <section className="bg-white border border-honey-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-honey-500" />
          <h2 className="font-display text-xl font-bold text-ink">تنظیمات سایت</h2>
        </div>
        <form onSubmit={saveSite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="عنوان سایت (فارسی)" value={siteForm.brand_name_fa || ""} onChange={(v) => setSiteForm({ ...siteForm, brand_name_fa: v })} placeholder="تحسین" testid="settings-brand-fa" />
          <F label="عنوان سایت (انگلیسی)" value={siteForm.brand_name_en || ""} onChange={(v) => setSiteForm({ ...siteForm, brand_name_en: v })} placeholder="Tahsin" testid="settings-brand-en" />
          <F label="شعار سایت" value={siteForm.tagline || ""} onChange={(v) => setSiteForm({ ...siteForm, tagline: v })} full placeholder="یک جمله کوتاه که در هدر/فوتر نمایش داده می‌شود" testid="settings-tagline" />
          <F label="شماره واتساپ (با کد کشور، بدون +)" value={siteForm.whatsapp_number || ""} onChange={(v) => setSiteForm({ ...siteForm, whatsapp_number: v })} placeholder="989120000000" testid="settings-whatsapp" />
          <F label="آدرس اینستاگرام" value={siteForm.instagram_url || ""} onChange={(v) => setSiteForm({ ...siteForm, instagram_url: v })} />
          <F label="آدرس فیسبوک" value={siteForm.facebook_url || ""} onChange={(v) => setSiteForm({ ...siteForm, facebook_url: v })} />
          <F label="شماره تماس اصلی (واتساپ)" value={siteForm.contact_phone || ""} onChange={(v) => setSiteForm({ ...siteForm, contact_phone: v })} />
          <F label="شماره ثبت‌شده در فیسبوک" value={siteForm.contact_phone_2 || ""} onChange={(v) => setSiteForm({ ...siteForm, contact_phone_2: v })} />
          <F label="ایمیل تماس" value={siteForm.contact_email || ""} onChange={(v) => setSiteForm({ ...siteForm, contact_email: v })} />
          <F label="آدرس" value={siteForm.address || ""} onChange={(v) => setSiteForm({ ...siteForm, address: v })} full />
          <F label="نام توسعه‌دهنده" value={siteForm.developer_name || ""} onChange={(v) => setSiteForm({ ...siteForm, developer_name: v })} />
          <F label="لینک پورتفولیو توسعه‌دهنده" value={siteForm.developer_url || ""} onChange={(v) => setSiteForm({ ...siteForm, developer_url: v })} />
          <div className="md:col-span-2">
            <label className="block text-sm font-display text-inkMuted mb-2">
              کلمات مارکی (هر کلمه در یک خط)
            </label>
            <textarea
              rows={4}
              value={marqueeInput}
              onChange={(e) => setMarqueeInput(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <Msg msg={siteMsg} />
            <button type="submit" disabled={savingSite} className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60" data-testid="settings-site-save">
              <Save className="w-4 h-4" /> {savingSite ? "در حال ذخیره..." : "ذخیره تنظیمات"}
            </button>
          </div>
        </form>
      </section>

      {/* Password Change */}
      <section className="bg-white border border-honey-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-5 h-5 text-honey-500" />
          <h2 className="font-display text-xl font-bold text-ink">تغییر رمز ادمین</h2>
        </div>
        <form onSubmit={submitPw} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="رمز فعلی" type="password" value={pwOld} onChange={setPwOld} testid="settings-pw-old" />
          <F label="رمز جدید" type="password" value={pwNew} onChange={setPwNew} testid="settings-pw-new" />
          <F label="تکرار رمز جدید" type="password" value={pwNew2} onChange={setPwNew2} testid="settings-pw-new2" />
          <div className="md:col-span-3 flex items-center justify-between">
            <Msg msg={pwMsg} />
            <button type="submit" disabled={pwBusy} className="inline-flex items-center gap-2 bg-ink hover:bg-olive-600 text-paper font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60" data-testid="settings-pw-save">
              <Save className="w-4 h-4" /> {pwBusy ? "در حال تغییر..." : "تغییر رمز"}
            </button>
          </div>
        </form>
      </section>

      {/* R2 Config */}
      <section className="bg-white border border-honey-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <KeyRound className="w-5 h-5 text-honey-500" />
          <h2 className="font-display text-xl font-bold text-ink">پیکربندی R2</h2>
        </div>
        <p className="text-sm text-inkMuted mb-5">
          فقط Public URL و Endpoint قابل تغییر هستند. برای تغییر کلیدهای R2، بهتر است از راه‌اندازی اولیه در باکت جدید استفاده کنید.
        </p>
        <form onSubmit={submitR2} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Public URL" value={r2Form.publicUrl} onChange={(v) => setR2Form({ ...r2Form, publicUrl: v })} />
          <F label="Endpoint" value={r2Form.endpoint} onChange={(v) => setR2Form({ ...r2Form, endpoint: v })} />
          <F label="رمز ادمین (تایید)" type="password" value={r2Pw} onChange={setR2Pw} full testid="settings-r2-pw" />
          <div className="md:col-span-2 flex items-center justify-between">
            <Msg msg={r2Msg} />
            <button type="submit" disabled={r2Busy} className="inline-flex items-center gap-2 bg-olive-600 hover:bg-olive-800 text-paper font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60" data-testid="settings-r2-save">
              <Save className="w-4 h-4" /> {r2Busy ? "در حال ذخیره..." : "به‌روزرسانی پیکربندی"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function F({ label, value, onChange, type = "text", placeholder, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}

